import type { ReactNode } from "react";
import { redirect, useSearchParams } from "react-router";
import type { ActionFunctionArgs } from "react-router";
import type { RequestBody, UpdateProfileRequest } from "@repo/shared-types";
import { api } from "@/lib/api";
import { forgetEverything } from "@/lib/query";
import { errorMessage, formError, safeNext } from "@/lib/session";
import { requireSession } from "@/lib/guards";
import { looksLikeAPicture, readCrop, uploadPicture } from "@/lib/images";
import { toast } from "@/lib/toast";
import { PageTitle } from "@/components/brand";
import { BackLink } from "@/components/navigation";

/**
 * What the pages under Account share (ADR 0068) — Profile, Password,
 * Division, Time zone, Appearance and Delete account: one action for every
 * form on them, and the header each starts with. What saved says so in a
 * toast, and an error stays on the page it is about.
 */

/** For a page that needs no more than who is asking: read with `useSession`. */
export async function sessionLoader({ request }: { request: Request }) {
  await requireSession(request);
  return null;
}

export type ActionResult = {
  /** The form it answers, so a message shows beside that form. */
  intent: string;
  error?: string;
  /** The box an error is about, shown beside it. */
  field?: string;
  /** Something to read that is not a save: where the link to set a password went. */
  note?: string;
  /** When the password changed, so its form starts empty again. */
  changedAt?: number;
};

/** What a toast says for a default saved by tapping it: one field at a time. */
function savedDefault(body: RequestBody<UpdateProfileRequest>): string {
  const sent = Object.entries(body);
  if (sent.length !== 1) return "Defaults saved.";
  const [name, value] = sent[0]!;
  const what = name === "division" ? "Division" : "Time zone";
  // Choosing "This device's" is a zone too; clearing the other two is not.
  return value === "" && name !== "timeZone" ? `${what} cleared.` : `${what} saved.`;
}

export async function accountAction({
  request,
}: ActionFunctionArgs): Promise<ActionResult | Response> {
  const form = await request.formData();
  const intent = String(form.get("intent") ?? "");

  // Your profile picture (7g): framed here, shrunk, posted to the store and
  // confirmed. It is on the session, so the cached one is stale after either.
  if (intent === "avatar") {
    const file = form.get("file");
    if (!looksLikeAPicture(file)) return { intent, error: "Choose a picture." };
    try {
      await uploadPicture({
        start: () => api.me.avatar.start(),
        confirm: (mediaId) => api.space("me").media.uploaded(mediaId),
        file,
        crop: readCrop(form.get("crop")),
      });
    } catch (error) {
      return { intent, error: errorMessage(error, "Could not save the picture") };
    }
    toast("Profile picture saved.");
    return { intent };
  }

  if (intent === "remove-avatar") {
    try {
      await api.me.avatar.remove();
    } catch (error) {
      return { intent: "avatar", error: errorMessage(error, "Could not remove the picture") };
    }
    toast("Profile picture removed. Your initials show instead.");
    return { intent: "avatar" };
  }

  // A default, sent by a fetcher the moment it is tapped: only the field
  // sent changes, and "" clears it.
  if (intent === "profile") {
    const body: RequestBody<UpdateProfileRequest> = {};
    for (const name of ["division", "timeZone"] as const) {
      const value = form.get(name);
      if (typeof value === "string") body[name] = value;
    }
    try {
      await api.me.profile.update(body);
    } catch (error) {
      return { intent, error: errorMessage(error, "Could not save your defaults") };
    }
    toast(savedDefault(body));
    return { intent };
  }

  // Changing it needs the current one; every other session ends (0030).
  if (intent === "password") {
    try {
      await api.me.changePassword({
        currentPassword: String(form.get("currentPassword") ?? ""),
        newPassword: String(form.get("newPassword") ?? ""),
      });
    } catch (error) {
      return { intent, ...formError(error, "Could not change the password") };
    }
    toast("Password changed. Every other device has been signed out.");
    return { intent, changedAt: Date.now() };
  }

  // An account that signs in with Google has no current password
  // to type, so its first is set by a link to its address, like a reset.
  // Not a toast: it says what to do next, which is worth the time to read.
  if (intent === "set-password") {
    try {
      await api.auth.forgotPassword(String(form.get("email") ?? ""));
    } catch (error) {
      return { intent: "password", error: errorMessage(error, "Could not send the link") };
    }
    return {
      intent: "password",
      note: "Check your email: the link to set a password works once and expires in an hour.",
    };
  }

  // Emptied, it clears: a name is optional (0076).
  if (intent === "name") {
    const name = String(form.get("name") ?? "").trim();
    try {
      await api.me.update({ name });
    } catch (error) {
      return { intent, error: errorMessage(error, "Could not change the name") };
    }
    toast(name ? "Name saved." : "Name cleared.");
    return { intent };
  }

  if (intent === "username") {
    try {
      await api.me.update({ username: String(form.get("username") ?? "").trim() });
    } catch (error) {
      return { intent, error: errorMessage(error, "Could not change the username") };
    }
    toast("Username changed.");
    return { intent };
  }

  if (intent !== "delete") throw new Response("Bad Request", { status: 400 });
  try {
    await api.me.delete({
      confirm: String(form.get("confirm") ?? "").trim(),
      // One checkbox per space, so the ids arrive as repeated fields.
      deleteSpaces: form.getAll("deleteSpaces").map(String),
    });
  } catch (error) {
    return { intent: "delete", error: errorMessage(error, "Could not delete the account") };
  }
  // The session row went with the account; drop every copy so nothing
  // renders as signed in on the way out.
  forgetEverything();
  // The signed-out page says nothing of it.
  toast("Your account is deleted.");
  return redirect("/");
}

/**
 * The top of every page under Account: the way back, and the page's name.
 * Back is to the list, or to the page a link came from (`useAccountLink`) —
 * only ever a path on this origin.
 */
export function AccountPage({ title, children }: { title: string; children: ReactNode }) {
  const [params] = useSearchParams();
  const back = safeNext(params.get("from"), "/account");
  return (
    <section className="space-y-6">
      <PageTitle of={[title, "Account"]} />
      <div className="space-y-2">
        <BackLink to={back}>{back === "/account" ? "Account" : "Back"}</BackLink>
        <h1 className="page-title">{title}</h1>
      </div>
      {children}
    </section>
  );
}
